import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Application } from './Application';
import { SurveillanceAbonnement } from './abonnement/surveillance';
import { FournisseurSession } from './auth/session';
import { FournisseurLangue } from './i18n/langue';
import { SynchronisationLangueCompte } from './i18n/SynchronisationLangueCompte';
// Les tokens de marque deviennent des variables CSS : une seule source pour le site, le
// web et le mobile. La feuille est ENGENDRÉE au build de `@repere/ui-tokens` à partir de
// `cssVariables` (`dark: true` : l'application porte les trois états — clair, sombre, et
// celui de l'appareil).
//
// Un import, et non plus une balise `<style>` posée à l'exécution : la politique de
// sécurité de contenu interdit `style-src 'unsafe-inline'`, et le bloc injecté était
// bloqué en environnement déployé — l'application perdait toutes ses couleurs. Servie
// depuis l'origine, la même feuille passe sans que la politique ait à céder.
//
// **Avant `global.css`, et l'ordre porte du sens** : cette dernière redéfinit certains
// jetons — l'échelle resserrée du téléphone — et ne peut le faire que si elle passe
// après. Les deux lignes ne se permutent pas.
import '@repere/ui-tokens/tokens.css';
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Le retour sur l'application redemande ce qu'elle affiche.
      //
      // C'était `false`, au motif que « le calendrier et les dossiers changent rarement
      // d'une seconde à l'autre ». C'est vrai de la donnée, faux de l'usage : on revient
      // sur Repère **parce qu'**il s'est passé quelque chose — un message reçu, une
      // dépense annoncée de vive voix. L'écran rouvert montrait alors l'état d'avant, et
      // il fallait passer d'un onglet à l'autre pour le voir changer.
      //
      // `staleTime` (30 s ci-dessous) reste le garde-fou : deux allers-retours dans la
      // même minute ne relancent rien. En coque, le signal du système prend le relais —
      // voir `brancherReprise`, car une WebView ne perd pas toujours le focus quand
      // l'application passe derrière.
      refetchOnWindowFocus: true,
      /**
       * L'écran ouvert se tient à jour tout seul.
       *
       * Le reste du produit n'a pas de temps réel : la passerelle WebSocket ne porte que
       * la messagerie. Une dépense déclarée, une proposition d'échange, un fait au
       * journal n'atteignaient donc un écran resté ouvert par aucun chemin — l'écran
       * affichait l'état qu'il avait au premier rendu, indéfiniment.
       *
       * Une minute, et non dix secondes : ce qui se passe dans un dossier se compte en
       * heures, pas en secondes. Ce délai suffit à ce qu'on ne voie jamais une donnée
       * franchement périmée, sans faire de chaque téléphone ouvert un client qui
       * interroge sans fin.
       *
       * `refetchIntervalInBackground` reste à son défaut, `false` : un téléphone dans une
       * poche n'interroge rien. Le rappel ne tourne que sous les yeux de quelqu'un, et le
       * retour au premier plan est déjà couvert par `brancherReprise`.
       */
      refetchInterval: 60_000,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

const racine = document.getElementById('racine');
if (!racine) throw new Error("Élément racine introuvable dans le document.");

createRoot(racine).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <FournisseurLangue>
        <FournisseurSession>
          <SynchronisationLangueCompte>
            <SurveillanceAbonnement>
              <Application />
            </SurveillanceAbonnement>
          </SynchronisationLangueCompte>
        </FournisseurSession>
      </FournisseurLangue>
    </QueryClientProvider>
  </StrictMode>,
);
